import { getStriveBaseUrl, striveHeaders } from "../striveApi";
import type { PatientMedication, ProPatientDto } from "./complianceTypes";
import { isValidTimeZone } from "./complianceClock";
import { parseProMedication } from "./complianceMeds";

// Keeps each request around a second on a cold cache; the endpoint accepts up to 500.
export const PRO_BATCH_SIZE = 100;
// In case a project grows past a few hundred patients.
export const PRO_MAX_CONCURRENT = 4;
// The list waits for PRO, so a slow batch is given up on rather than waited out.
export const PRO_TIMEOUT_MS = 10_000;

export class ProLoadError extends Error {
  constructor(readonly status: number) {
    super(`Failed to load PRO data (${status})`);
    this.name = "ProLoadError";
  }
}

export interface ParsedProPatient {
  patientId: string;
  timezone: string;
  timezoneMissing: boolean;
  medications: PatientMedication[];
  bowelMovementLastLoggedAt: string | null;
}

export function parseProPatient(p: ProPatientDto): ParsedProPatient {
  // A zone the browser doesn't know is treated like a missing one: days in UTC, flagged.
  const timezoneMissing = p.timezone_missing || !isValidTimeZone(p.timezone);
  return {
    patientId: p.patient_id,
    timezone: timezoneMissing ? "UTC" : p.timezone,
    timezoneMissing,
    medications: p.medications.map(parseProMedication),
    bowelMovementLastLoggedAt: p.bowel_movement.last_logged_at
  };
}

export type ProBatchResult =
  | { patientIds: string[]; patients: ParsedProPatient[] }
  | { patientIds: string[]; error: unknown };

// POST /v3/compliance/pro: a read, sent as POST because the IDs don't fit in a URL. Patients
// without a Strive user, or that the user can't read, are left out of the response.
export async function fetchProBatch(
  patientIds: string[]
): Promise<ParsedProPatient[]> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PRO_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${getStriveBaseUrl()}/api/v3/compliance/pro`,
      {
        method: "POST",
        headers: {
          ...(await striveHeaders()),
          "Content-Type": "application/json"
        },
        // No `types`, so both come back: bowel movements can be turned on for the session.
        body: JSON.stringify({ patient_ids: patientIds }),
        signal: controller.signal
      }
    );
    if (!response.ok) throw new ProLoadError(response.status);
    // A body that isn't JSON is malformed, but a timeout while reading it is still a timeout.
    const body = await response.json().catch((error: unknown) => {
      if (controller.signal.aborted) throw error;
      return null;
    });
    const patients = body?.data?.patients;
    if (!Array.isArray(patients)) throw new ProLoadError(response.status);
    return (patients as ProPatientDto[]).map(parseProPatient);
  } finally {
    clearTimeout(timeout);
  }
}

export function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

// A failed batch doesn't stop the others.
export async function fetchCompliancePro(
  patientIds: string[]
): Promise<ProBatchResult[]> {
  const batches = chunk(patientIds, PRO_BATCH_SIZE);
  const results: ProBatchResult[] = [];
  // Shared by the workers, so each batch is sent once.
  let next = 0;
  const worker = async () => {
    while (next < batches.length) {
      const ids = batches[next++];
      results.push(
        await fetchProBatch(ids).then(
          (patients): ProBatchResult => ({ patientIds: ids, patients }),
          (error: unknown): ProBatchResult => ({ patientIds: ids, error })
        )
      );
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(PRO_MAX_CONCURRENT, batches.length) }, worker)
  );
  return results;
}
